import {
  faArrowsRotate,
  faClock,
  faNoteMedical,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { type ReactNode } from "react";

import {
  DSBadge,
  DSBadgeVariant,
  DSCardWrapper,
  DSCircularButton,
} from "../molecules";

export type DSNoteItemSize = "full" | "compact";
export type DSNoteItemVariant = "default" | "unsynced";
export type DSNoteItemStatus =
  | "review"
  | "processing"
  | "unprocessed"
  | "unsynced"
  | "syncing"
  | "export"
  | "exported";

const statusConfig: Record<
  DSNoteItemStatus,
  { label: string; variant: DSBadgeVariant }
> = {
  review: { label: "Review", variant: "green" },
  processing: { label: "Processing", variant: "yellow" },
  unprocessed: { label: "Unprocessed", variant: "red" },
  unsynced: { label: "Unsynced", variant: "red" },
  syncing: { label: "Syncing", variant: "cyan" },
  export: { label: "Export", variant: "blue" },
  exported: { label: "Exported", variant: "brand" },
};

export interface DSNoteItemProps {
  /** Title of the note (e.g., patient name and chief complaint) */
  title: string;
  /** Template name used for this note */
  templateName: string;
  /** Date of the note */
  date: string;
  /** Size variant */
  size?: DSNoteItemSize;
  /** State variant */
  variant?: DSNoteItemVariant;
  /** Status determines the badge displayed */
  status?: DSNoteItemStatus;
  /** Click handler for the card */
  onClick?: () => void;
  /** Click handler for the sync button (unsynced variant only) */
  onSyncClick?: () => void;
  dataTestId: string;
  ref?: React.Ref<HTMLDivElement>;
  /** Patient info area — renders above title row, replaces template line when present */
  subtitle?: ReactNode;
  /** Custom status area — takes precedence over `status` enum */
  statusSlot?: ReactNode;
  /** Below card content (e.g., AudioPlayer) */
  footer?: ReactNode;
  /** Custom icon before title (replaces default faNoteMedical) */
  titleIcon?: ReactNode;
  /** Owner display name shown bottom-right of title row */
  ownerName?: string;
  /** Wraps card in <a> for link semantics */
  href?: string;
  /** Test ID on template name element */
  dataTestIdTemplate?: string;
  /** Test ID on title row */
  dataTestIdTitle?: string;
  /** Rendered as data-note-statuses attribute on root */
  noteStatuses?: string[];
  /** Additional CSS class on root */
  className?: string;
  /** Hide the title row when note has no title */
  hideTitle?: boolean;
}

/**
 * DSNoteItem - Note card component for displaying clinical notes
 *
 * A card component that displays note information including title, template, and date.
 * Supports different sizes and states for various use cases.
 */
export const DSNoteItem = ({
  title,
  templateName,
  date,
  size = "full",
  variant = "default",
  status,
  onClick,
  onSyncClick,
  dataTestId,
  ref,
  subtitle,
  statusSlot,
  footer,
  titleIcon,
  ownerName,
  href,
  dataTestIdTemplate,
  dataTestIdTitle,
  noteStatuses,
  className,
  hideTitle,
}: DSNoteItemProps) => {
  const isCompact = size === "compact";
  const isUnsynced = variant === "unsynced";

  // Only show built-in badge if no statusSlot override and status is valid
  const showBadge =
    !statusSlot &&
    status &&
    (status !== "unsynced" || isUnsynced) &&
    !isCompact;
  const badgeConfig = status ? statusConfig[status] : null;

  const cardVariant = isUnsynced ? "ghost" : "default";
  const dimensions = isCompact ? "w-84 h-20" : "w-full";
  const contentGap = isCompact ? "gap-xs" : "gap-sm";
  const textSize = isCompact ? "text-xs" : "text-sm";

  const handleSyncClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSyncClick?.();
  };

  const showTitleRow = !hideTitle || !!title || !!ownerName;

  const cardContent = (
    <>
      <div className="flex items-center gap-md">
        <div className={`flex flex-col ${contentGap} flex-1`}>
          {/* Info row: subtitle or template+date on the left, status on the right */}
          <div className="flex items-center">
            <div className="flex-1 text-xs text-secondary">
              {subtitle || (
                <div className="flex items-center gap-sm">
                  <p
                    className={`shrink-0 m-0 ${
                      isCompact ? "text-xxs" : "text-xs"
                    }`}
                    data-testid={dataTestIdTemplate}
                  >
                    {templateName}
                  </p>
                  <p
                    className={`shrink-0 m-0 ${
                      isCompact ? "text-xxs" : "text-xs"
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={faClock}
                      className="text-secondary"
                    />{" "}
                    {date}
                  </p>
                </div>
              )}
            </div>
            {statusSlot ? (
              <div className="flex shrink-0">{statusSlot}</div>
            ) : (
              showBadge &&
              badgeConfig && (
                <div className="flex shrink-0">
                  <DSBadge
                    variant={badgeConfig.variant}
                    size="sm"
                    dataTestId={`${dataTestId}-badge`}
                  >
                    {badgeConfig.label}
                  </DSBadge>
                </div>
              )
            )}
          </div>

          {/* Title row */}
          {showTitleRow && (
            <div
              className="flex items-center gap-xs"
              data-testid={dataTestIdTitle}
            >
              <div className="flex items-center gap-xs flex-1">
                {!isUnsynced &&
                  (titleIcon || (
                    <FontAwesomeIcon
                      icon={faNoteMedical}
                      className={`text-brand shrink-0 ${textSize}`}
                    />
                  ))}
                <p
                  className={`font-title font-semibold leading-tight line-clamp-2 text-primary ${textSize}`}
                >
                  {isUnsynced
                    ? "This note is not yet synced to our servers. Click to sync."
                    : title}
                </p>
              </div>
              {ownerName && (
                <span className="text-xs text-secondary shrink-0">
                  {ownerName}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Sync button - only in unsynced variant */}
        {isUnsynced && (
          <span
            className={status === "syncing" ? `animate-spin` : ""}
          >
            <DSCircularButton
              icon={faArrowsRotate}
              size="sm"
              onClick={handleSyncClick}
              aria-label="Sync note to server"
              dataTestId={`${dataTestId}-sync-btn`}
            />
          </span>
        )}
      </div>

      {/* Footer slot (e.g., AudioPlayer) */}
      {footer && <div className="mt-sm">{footer}</div>}
    </>
  );

  return (
    <div
      ref={ref}
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
      data-note-statuses={noteStatuses?.join(" ")}
      className={`relative ${dimensions} ${className || ""}`}
    >
      <DSCardWrapper
        variant={cardVariant}
        interactive={!!onClick}
        onClick={onClick}
        padding="p-md"
        aria-label={onClick ? `View note: ${title}` : undefined}
        dataTestId={`${dataTestId}-card`}
      >
        {href ? (
          <a
            href={href}
            rel="noopener noreferrer"
            onClick={(e) => {
              // Only prevent default on plain left-click (SPA navigation via onClick)
              // Allow ctrl+click, meta+click, middle-click for native "open in new tab"
              if (
                !e.metaKey &&
                !e.ctrlKey &&
                !e.shiftKey &&
                e.button === 0
              ) {
                e.preventDefault();
              }
            }}
            className="no-underline! text-inherit"
          >
            {cardContent}
          </a>
        ) : (
          cardContent
        )}
      </DSCardWrapper>
    </div>
  );
};

DSNoteItem.displayName = "DSNoteItem";
